import { useEffect, useState } from "react";
import { Link, useNavigate, Outlet, useLocation } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { ROLE_LABELS } from "@/lib/constants";
import {
  MagnifyingGlass, SignOut, UserCircle, List, X, SlidersHorizontal, CaretDoubleLeft, CaretDoubleRight, PencilSimple, Gear, Users,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ProfileDialog from "@/components/ProfileDialog";
import SettingsDialog from "@/components/SettingsDialog";
import LetterWizard from "@/components/LetterWizard";
import MessagingDialog from "@/components/MessagingDialog";
import { PostPrintHost } from "@/components/messaging/PostPrintDialog";
import MailboxWatcher from "@/components/communication/MailboxWatcher";
import { SidebarNav } from "@/components/layout/SidebarNav";


export default function Layout() {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [sideOpen, setSideOpen] = useState(false);
  const [postfachCount, setPostfachCount] = useState(0);
  const [navCollapsed, setNavCollapsed] = useState(() => localStorage.getItem("domivus.nav.collapsed") === "1");
  const toggleCollapsed = () => setNavCollapsed((v) => { localStorage.setItem("domivus.nav.collapsed", v ? "0" : "1"); return !v; });
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const h = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  const [profileOpen, setProfileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // VM-7d — Standalone-Schreiben: Wizard + Composer
  const [wizardOpen, setWizardOpen] = useState(false);
  const [composerState, setComposerState] = useState({ open: false, context: null, recipients: [], bezugType: null, bezugEntity: null, rundschreibenMode: "per_empfaenger" });

  useEffect(() => {
    setQ("");
    setSideOpen(false);
  }, [location.pathname]);

  // 2026-02 (iter122) — Mobile Swipe-Geste: von linkem Bildschirmrand nach
  // rechts wischen öffnet das Menü; auf offenem Drawer nach links wischen
  // schließt es. Nur auf Touch-Devices aktiv (lg:hidden Bereich).
  useEffect(() => {
    let touchStartX = null;
    let touchStartY = null;
    let touchStartTime = 0;
    const EDGE_ZONE = 25;     // Pixel vom linken Rand für „Menü öffnen"
    const MIN_SWIPE_X = 60;   // Mindest-Horizontal-Distanz für Swipe
    const MAX_SWIPE_Y = 40;   // Max vertikale Abweichung (sonst = Scroll)
    const MAX_TIME = 500;     // Max Dauer der Geste in ms

    const onStart = (e) => {
      const t = e.touches[0];
      touchStartX = t.clientX;
      touchStartY = t.clientY;
      touchStartTime = Date.now();
    };
    const onEnd = (e) => {
      if (touchStartX == null) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStartX;
      const dy = Math.abs(t.clientY - touchStartY);
      const dt = Date.now() - touchStartTime;
      if (dt > MAX_TIME || dy > MAX_SWIPE_Y) { touchStartX = null; return; }
      // Menü öffnen: Wisch von links (edge) nach rechts
      if (touchStartX < EDGE_ZONE && dx > MIN_SWIPE_X && !sideOpen) {
        setSideOpen(true);
      }
      // Menü schließen: bei offenem Drawer nach links wischen
      if (sideOpen && dx < -MIN_SWIPE_X) {
        setSideOpen(false);
      }
      touchStartX = null;
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchend", onEnd);
    };
  }, [sideOpen]);

  // 2026-07 — Globaler Composer-Trigger via CustomEvent, damit z. B. der
  // „Schreiben verfassen"-Button aus einem Vorgang-Detail den Composer
  // direkt mit vorgang_id + Auto-Nummer öffnen kann, ohne den Wizard.
  useEffect(() => {
    const handler = (ev) => {
      const detail = ev.detail || {};
      setComposerState({
        open: true,
        context: detail.context || {},
        recipients: detail.recipients || [],
        rundschreiben: !!detail.rundschreiben,
        rundschreibenMode: detail.rundschreibenMode || "per_empfaenger",
        bezugType: detail.bezugType || null,
        bezugEntity: detail.bezugEntity || null,
        attachmentDocIds: detail.attachmentDocIds || [],
        myReference: detail.myReference || "",
      });
    };
    window.addEventListener("domivus:open-composer", handler);
    return () => window.removeEventListener("domivus:open-composer", handler);
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/suche?q=${encodeURIComponent(q.trim())}`);
  };

  const isAdmin = user && ["super_admin", "admin"].includes(user.role);
  const activeModules = new Set(tenant?.modules || []);
  const isVisible = (n) => {
    if (n.adminOnly && !isAdmin) return false;
    if (n.module && activeModules.size > 0 && !activeModules.has(n.module)) return false;
    return true;
  };
  const brandName = tenant?.display_name || "Hausverwaltung";
  const brandInitial = (brandName.match(/\b[A-ZÄÖÜ]/g) || ["H"]).join("").slice(0, 2);

  const renderSidebar = (desktop) => {
    const collapsed = desktop && navCollapsed;
    const logoSrc = tenant?.logo_url ? (tenant.logo_url.startsWith("http") ? tenant.logo_url : `${process.env.REACT_APP_BACKEND_URL || ""}${tenant.logo_url}`) : null;
    return (
      <aside
        className={`sidebar ${collapsed ? "w-16" : "w-60"} transition-[width] duration-200 shrink-0 border-r border-stone-200 bg-white flex flex-col h-full lg:h-screen lg:sticky lg:top-0`}
        data-testid="app-sidebar"
      >
        <div className={`h-14 flex items-center border-b border-stone-200 bg-white relative ${collapsed ? "justify-center px-2" : "px-4"}`}>
          <Link to="/" className="flex items-center gap-2 w-full overflow-hidden justify-center lg:justify-start" data-testid="brand-logo" title={brandName}>
            {collapsed ? (
              <span className="h-9 w-9 mx-auto rounded-md bg-slate-900 text-white grid place-items-center text-sm font-bold" data-testid="brand-logo-mini">{brandInitial}</span>
            ) : logoSrc ? (
              <img src={logoSrc} alt={brandName} className="max-h-9 w-auto max-w-full object-contain" data-testid="brand-logo-img" />
            ) : (
              <>
                <img src="/logo.svg" alt="Domivus" className="w-9 h-9 shrink-0" />
                <span className="font-semibold tracking-tight text-slate-900 truncate">{brandName}</span>
              </>
            )}
          </Link>
          <button type="button" className="lg:hidden absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md hover:bg-slate-100" onClick={() => setSideOpen(false)} aria-label="Menü schließen">
            <X size={18} />
          </button>
        </div>
        <div className={collapsed ? "px-2 pt-3 pb-1" : "px-3 pt-3 pb-1"}>
          <Button
            onClick={() => { setWizardOpen(true); setSideOpen(false); }}
            className={`w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-9 font-semibold ${collapsed ? "px-0" : ""}`}
            title="Schreiben"
            data-testid="sidebar-schreiben-btn"
          >
            <PencilSimple size={16} weight="bold" />{!collapsed && "Schreiben"}
          </Button>
        </div>
        <SidebarNav isVisible={isVisible} collapsed={collapsed} postfachCount={postfachCount} />
        <div className={`border-t border-stone-200 flex items-center ${collapsed ? "justify-center py-2" : "justify-between pl-5 pr-2 py-2"}`}>
          {!collapsed && <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">powered by Domivus</span>}
          {desktop && (
            <button type="button" onClick={toggleCollapsed} title={collapsed ? "Menü ausklappen" : "Menü einklappen"}
              className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors" data-testid="sidebar-collapse-toggle">
              {collapsed ? <CaretDoubleRight size={16} /> : <CaretDoubleLeft size={16} />}
            </button>
          )}
        </div>
      </aside>
    );
  };

  return (
    <div className="h-screen flex bg-stone-100 text-slate-900 overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">{isDesktop && renderSidebar(true)}</div>

      {/* Mobile drawer */}
      {sideOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-slate-900/40" onClick={() => setSideOpen(false)} />
          <div className="relative z-50 h-full">{renderSidebar(false)}</div>
        </div>
      )}

      {/* Main column — eigenständig scrollbar, Sidebar bleibt sticky */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Topbar */}
        <header className="h-14 bg-white border-b border-stone-200 flex items-center px-3 sm:px-6 gap-2 sm:gap-4">
          <button
            type="button"
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-md hover:bg-slate-100"
            onClick={() => setSideOpen(true)}
            aria-label="Menü"
            data-testid="staff-menu-toggle"
          >
            <List size={22} />
          </button>
          <form onSubmit={onSearch} className="flex-1 max-w-xl">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Suche..."
                className="pl-9 bg-slate-50 border-slate-200 focus-visible:ring-slate-900"
                data-testid="global-search-input"
              />
            </div>
          </form>

          <div className="ml-auto flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2 px-2 hover:bg-slate-100" data-testid="user-menu-trigger">
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url.startsWith("http")
                      ? user.avatar_url
                      : `${process.env.REACT_APP_BACKEND_URL || ""}${user.avatar_url}`}
                    alt=""
                    className="h-7 w-7 rounded-full object-cover"
                    data-testid="header-avatar"
                  />
                ) : (
                  <UserCircle size={22} weight="regular" />
                )}
                <div className="text-left hidden sm:block">
                  <div className="text-sm font-medium leading-tight truncate max-w-[140px]">{user?.name}</div>
                  <div className="text-[11px] text-slate-500 leading-tight">{ROLE_LABELS[user?.role] || user?.role}</div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/mein-profil")} data-testid="menu-profile">
                <UserCircle size={16} className="mr-2" />
                Mein Profil
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSettingsOpen(true)} data-testid="menu-personal-settings">
                <SlidersHorizontal size={16} className="mr-2" />
                Persönliche Einstellungen
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate("/einstellungen")} data-testid="menu-admin-settings">
                  <Gear size={16} className="mr-2" />
                  Einstellungen
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate("/benutzer")} data-testid="menu-users">
                  <Users size={16} className="mr-2" />
                  Benutzerverwaltung
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout">
                <SignOut size={16} className="mr-2" />
                Abmelden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="ghost"
            size="icon"
            className="hover:bg-slate-100"
            onClick={() => setSettingsOpen(true)}
            title="Persönliche Einstellungen"
            data-testid="personal-settings-btn"
          >
            <SlidersHorizontal size={18} />
          </Button>
          </div>

          <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
          <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* 2026-02 (iter122) — ErrorBoundary fängt Render-Fehler pro Route ab,
              damit ein Bug in einer einzelnen Seite nicht die gesamte App
              weißmacht. Sidebar/Header bleiben funktional; der User kann
              per Klick zur Startseite zurück oder die Seite neu laden. */}
          <ErrorBoundary key={location.pathname}>
            <Outlet />
            <PostPrintHost />
            <MailboxWatcher onCount={setPostfachCount} />
          </ErrorBoundary>
        </main>
      </div>

      {/* VM-7d — Standalone-Schreiben: Wizard + Composer */}
      <LetterWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onOpenComposer={(payload) => {
          setComposerState({
            open: true,
            context: payload.context || {},
            recipients: payload.recipients || [],
            bezugType: payload.bezugType,
            bezugEntity: payload.bezugEntity,
            rundschreiben: !!payload.rundschreiben,
            rundschreibenMode: payload.rundschreibenMode || "per_empfaenger",
          });
        }}
      />
      <MessagingDialog
        open={composerState.open}
        onOpenChange={(v) => setComposerState((s) => ({ ...s, open: v }))}
        context={composerState.context || {}}
        wizardRecipients={composerState.recipients}
        rundschreiben={composerState.rundschreiben}
        rundschreibenMode={composerState.rundschreibenMode}
        initialAttachmentDocIds={composerState.attachmentDocIds}
        initialMyReference={composerState.myReference}
      />
    </div>
  );
}
